import * as React from 'react';
import { Dispatch } from 'react';
import cn from 'classnames';

import {
	CheckinSaveOrderServicesMutationVariables,
	InsuranceProgramFragment,
	SaveOrderServicesMutationVariables
} from '@websky/graphql';
import { Money } from '../../../types';
import { InsurancePageContext } from '../../InsurancePageContext';
import { useInsurancePage } from './InsurancePage.hook';
import { InsuranceProduct } from '../InsuranceProduct/InsuranceProduct';
import { Card } from '../Card/Card';
import { useTheme } from '../../../theme';
import { useTranslation } from 'react-i18next';
import SimpleLoader from '../../../SimpleLoader';
import { useRenderers } from '../../../renderProps';

export interface Props {
	className?: string;
	availablePrograms?: InsuranceProgramFragment[];
	onTotalInsurancePriceChange?: Dispatch<Money>;
	onServicesChanged?: Dispatch<boolean>;
	onSaveOrderServicesParamsChange?: Dispatch<
		SaveOrderServicesMutationVariables | CheckinSaveOrderServicesMutationVariables
	>;
	onDownloadNasInsurance?: () => Promise<void>;
	isShowImportantInsurancesError?: boolean;
}

export const InsurancePage: React.FC<Props> = ({
	className,
	availablePrograms,
	onTotalInsurancePriceChange,
	onSaveOrderServicesParamsChange,
	onServicesChanged,
	onDownloadNasInsurance,
	isShowImportantInsurancesError
}) => {
	const { InsurancePage: theme } = useTheme('InsurancePage');
	const { t } = useTranslation('InsurancePage');

	const model = useInsurancePage({
		availablePrograms,
		onSaveOrderServicesParamsChange,
		onTotalInsurancePriceChange,
		onServicesChanged
	});

	const { InsuranceProduct: InsuranceProductOverride = InsuranceProduct } = useRenderers();

	return (
		<InsurancePageContext.Provider value={model}>
			<Card className={cn(theme.wrapper, className)}>
				<Card className={theme.header}>
					<p className={theme.subheader}>
						{t(
							'To make your travels memorable only with pleasant excitement, take out an insurance policy that will protect you during your trip.'
						)}
					</p>
				</Card>
				<Card className={theme.content}>
					{model.mainPrograms.length > 0 && (
						<Card className={theme.mainInsurancesList}>
							{model.mainPrograms.map(importantProgram => (
								<InsuranceProductOverride
									key={importantProgram.code}
									hideForAllPassengersTextBecauseSinglePassenger={
										model.doNotRenderForAllPassengersText
									}
									productState={model.getProductState(importantProgram.code)}
									policyUrl={model.getPolicyUrl(importantProgram.code)}
									onSelect={model.select}
									onRemove={model.remove}
									onToggle={model.toggle}
									onDownloadVskInsurance={onDownloadNasInsurance}
									insuranceProgram={importantProgram}
									isShowImportantInsurancesError={isShowImportantInsurancesError}
									popular={true}
								/>
							))}
						</Card>
					)}

					{model.insurancesWithExcludedMainPrograms.length > 0 && (
						<Card className={theme.insurancesList}>
							{model.insurancesWithExcludedMainPrograms.map(insurance => (
								<InsuranceProductOverride
									key={insurance.code}
									hideForAllPassengersTextBecauseSinglePassenger={
										model.doNotRenderForAllPassengersText
									}
									productState={model.getProductState(insurance.code)}
									policyUrl={model.getPolicyUrl(insurance.code)}
									onSelect={model.select}
									onRemove={model.remove}
									onToggle={model.toggle}
									onDownloadVskInsurance={onDownloadNasInsurance}
									insuranceProgram={insurance}
									popular={false}
								/>
							))}
						</Card>
					)}
				</Card>
				{model.isLoading && <SimpleLoader />}
			</Card>
		</InsurancePageContext.Provider>
	);
};
